import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { planLabel } from "@/lib/plans";

/**
 * Shown instead of the app when a paid plan has lapsed: the payment failed
 * and the grace period ran out, or a cancelled plan reached its end date.
 * Lists stay safe; choosing a plan brings everything back.
 */
export default function PlanRequired() {
  const { user } = useAuth();
  const failed = user?.subscription_status === "past_due";
  return (
    <div className="flex h-full items-center justify-center overflow-y-auto px-6 py-10">
      <div className="w-full max-w-lg rounded-2xl border border-border bg-surface p-8 text-center shadow-card" data-testid="plan-required">
        <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-violet-soft text-violet-text">
          <Lock className="h-6 w-6" />
        </span>
        <h1 className="mt-5 font-display text-[28px] leading-tight tracking-[-0.02em]">
          {failed ? "Your payment didn't go through" : `Your ${planLabel(user?.plan)} plan has ended`}
        </h1>
        <p className="mt-2 text-[15px] leading-6 text-ink-secondary">
          {failed
            ? "Update your payment method to pick up where you left off."
            : "Choose a plan to keep revealing contacts and enriching lists."}{" "}
          Your saved lists and revealed contacts are kept.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <Button variant="outline" asChild><Link to="/billing">Billing</Link></Button>
          <Button asChild><Link to="/choose-plan">Choose a plan</Link></Button>
        </div>
      </div>
    </div>
  );
}

/** True when the plan has lapsed and the app should show PlanRequired. */
export function planHasLapsed(user) {
  if (!user || !user.plan || user.plan === "free") return false;
  const ended = user.current_period_end && new Date(user.current_period_end) < new Date();
  return Boolean(ended && (user.subscription_status === "past_due" || user.subscription_status === "canceled"));
}
